import { ImageWithFallback } from "@/components/image/ImageWithFallback"
import { Price, Surface, Text } from "@/components/ui"
import { cn } from "@/lib/cn"
import type { ReactNode } from "react"
import { ItemNote, ItemOptionsList, type OrderItemLineOption } from "./ItemOptionsList"

export type { OrderItemLineOption } from "./ItemOptionsList"

/** Props of `OrderItemLine`; a zero `price` reads "Grátis" (omit it to hide the price), `image` adds the thumbnail. */
export type OrderItemLineProps = {
  name: string
  quantity: number
  price?: number
  image?: { src?: string; crc?: string }
  options?: OrderItemLineOption[]
  note?: string
  quantityDisplay?: "always" | "multiple"
  /** `muted` (history lists) or `emphasis` (bold `secondary`, the order detail and the checkout item sheet). */
  priceTone?: "muted" | "emphasis"
}

/** Divided list wrapper for `OrderItemLine` rows. */
export function OrderItemList({ children, className }: { children: ReactNode; className?: string }) {
  return <ul className={cn("flex flex-col divide-y divide-border", className)}>{children}</ul>
}

/** Item row shared by the orders list, the order detail, the tracking page and the checkout item sheet. */
export function OrderItemLine({
  name,
  quantity,
  price,
  image,
  options = [],
  note,
  quantityDisplay = "multiple",
  priceTone = "muted",
}: OrderItemLineProps) {
  const showQuantity = quantityDisplay === "always" || quantity > 1
  return (
    <li className="flex gap-3 py-3 first:pt-0 last:pb-0">
      {image ? (
        <Surface surface="card" className="h-16 w-16 shrink-0 overflow-hidden rounded-control border border-border">
          <ImageWithFallback
            width={200}
            height={200}
            src={image.src}
            crc={image.crc}
            alt={name}
            fit="contain"
            className="h-full w-full"
          />
        </Surface>
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-baseline justify-between gap-3">
          <Text weight="semibold" tone="default" className="min-w-0">
            {showQuantity ? (
              <>
                <Text as="span" tone="muted">
                  {quantity}x
                </Text>{" "}
              </>
            ) : null}
            {name}
          </Text>
          {price !== undefined ? (
            <Price
              value={price}
              free
              tone={priceTone === "emphasis" ? "secondary" : price > 0 ? "muted" : undefined}
              weight={priceTone === "emphasis" ? "semibold" : undefined}
              className="shrink-0"
            />
          ) : null}
        </div>
        <ItemOptionsList options={options} />
        <ItemNote note={note} />
      </div>
    </li>
  )
}
